"use client";

import { useState, type FormEvent } from "react";

import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import type { PlaceResult } from "@/lib/api/misc";
import {
  rectifyBirthTime,
  type RectificationEventType,
  type RectificationResponse,
} from "@/lib/api/rectification";
import { EVENT_LABELS, EVENT_TYPE_IDS, MIN_USEFUL_EVENTS } from "./rectification-events";

interface EventRow {
  id: number;
  eventDate: string;
  eventType: RectificationEventType;
}

function newRow(id: number): EventRow {
  return { id, eventDate: "", eventType: "marriage" };
}

const FIELD_CLASS =
  "w-full rounded-xl border border-border-default bg-white p-3 text-sm text-text-primary";
const LABEL_CLASS = "mb-1 block text-xs font-bold uppercase tracking-wide text-text-primary";

function EventRows({
  rows,
  setRows,
  isHi,
}: {
  rows: EventRow[];
  setRows: (update: (current: EventRow[]) => EventRow[]) => void;
  isHi: boolean;
}) {
  const patch = (id: number, changes: Partial<EventRow>) =>
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...changes } : row)));

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.id} className="grid grid-cols-2 gap-2">
          <input
            type="date"
            value={row.eventDate}
            aria-label={isHi ? "घटना की तिथि" : "Event date"}
            onChange={(event) => patch(row.id, { eventDate: event.target.value })}
            className={FIELD_CLASS}
          />
          <select
            value={row.eventType}
            aria-label={isHi ? "घटना का प्रकार" : "Event type"}
            onChange={(event) =>
              patch(row.id, { eventType: event.target.value as RectificationEventType })
            }
            className={FIELD_CLASS}
          >
            {EVENT_TYPE_IDS.map((id) => (
              <option key={id} value={id}>
                {isHi ? EVENT_LABELS[id].hi : EVENT_LABELS[id].en}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}

export default function RectificationForm({
  isHi,
  onResult,
}: {
  isHi: boolean;
  onResult: (result: RectificationResponse | null) => void;
}) {
  const [birthDate, setBirthDate] = useState("");
  const [windowStart, setWindowStart] = useState("14:00");
  const [windowEnd, setWindowEnd] = useState("15:00");
  const [birthPlace, setBirthPlace] = useState("");
  const [place, setPlace] = useState<PlaceResult | null>(null);
  const [rows, setRows] = useState<EventRow[]>([newRow(1), newRow(2), newRow(3)]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  function validate(dated: EventRow[]): string | null {
    if (!place) return isHi ? "जन्म स्थान सूची से चुनें।" : "Pick your birth place from the list.";
    if (dated.length === 0) {
      return isHi ? "कम से कम एक घटना की तिथि दें।" : "Add the date of at least one life event.";
    }
    return null;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const dated = rows.filter((row) => row.eventDate);
    const invalid = validate(dated);
    setError(invalid);
    if (invalid || !place) return;

    setIsLoading(true);
    try {
      onResult(
        await rectifyBirthTime({
          birth_date: birthDate,
          window_start: windowStart,
          window_end: windowEnd,
          latitude: place.latitude,
          longitude: place.longitude,
          timezone: place.timezone,
          events: dated.map((row) => ({
            event_date: row.eventDate,
            event_type: row.eventType,
          })),
        })
      );
    } catch (err) {
      onResult(null);
      setError(
        err instanceof Error
          ? err.message
          : isHi
            ? "परिशोधन अभी पूर्ण नहीं हो सका।"
            : "Rectification could not be completed."
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card-sacred mb-6 space-y-4 p-5 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className={LABEL_CLASS}>{isHi ? "जन्म तिथि" : "Birth date"}</span>
          <input
            type="date"
            required
            value={birthDate}
            onChange={(event) => setBirthDate(event.target.value)}
            className={FIELD_CLASS}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className={LABEL_CLASS}>{isHi ? "समय से" : "From"}</span>
            <input
              type="time"
              required
              value={windowStart}
              onChange={(event) => setWindowStart(event.target.value)}
              className={FIELD_CLASS}
            />
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>{isHi ? "समय तक" : "To"}</span>
            <input
              type="time"
              required
              value={windowEnd}
              onChange={(event) => setWindowEnd(event.target.value)}
              className={FIELD_CLASS}
            />
          </label>
        </div>
      </div>

      <div>
        <span className={LABEL_CLASS}>{isHi ? "जन्म स्थान" : "Birth place"}</span>
        <PlaceAutocomplete
          value={birthPlace}
          onChange={setBirthPlace}
          onSelect={(selected) => {
            setPlace(selected);
            setBirthPlace(selected.display);
          }}
          required
          placeholder={isHi ? "शहर खोजें" : "Search for a city"}
        />
      </div>

      <fieldset className="border-t border-border-subtle pt-4">
        <legend className="text-xs font-bold uppercase tracking-wide text-text-primary">
          {isHi ? "जीवन की घटनाएं" : "Life events"}
        </legend>
        <p className="mb-3 mt-1 text-caption leading-relaxed text-text-muted">
          {isHi
            ? `कम से कम ${MIN_USEFUL_EVENTS} घटनाएं दें — उससे कम पर परिणाम केवल संकेत रहता है।`
            : `Add at least ${MIN_USEFUL_EVENTS} events — below that the ranking stays indicative only.`}
        </p>

        <EventRows rows={rows} setRows={setRows} isHi={isHi} />

        <button
          type="button"
          onClick={() => setRows((current) => [...current, newRow(Date.now())])}
          className="mt-2 text-xs font-semibold text-text-secondary underline"
        >
          {isHi ? "+ और घटना जोड़ें" : "+ Add another event"}
        </button>
      </fieldset>

      {error && (
        <p role="alert" className="text-xs font-medium text-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full rounded-xl bg-surface-brand-strong px-6 py-3 text-sm font-bold text-text-on-brand transition-all hover:bg-surface-brand-strong-hover disabled:opacity-60"
      >
        {isLoading
          ? isHi
            ? "गणना हो रही है…"
            : "Calculating…"
          : isHi
            ? "संभावित जन्म समय देखें"
            : "Rank candidate birth times"}
      </button>
    </form>
  );
}
